import { useState } from "react";
import { Button } from "../../components/Button";
import { Dialog } from "../../components/Dialog";
import { Input } from "../../components/Input";
import { useToast } from "../../components/Toast";
import { formatTaipeiDateTime } from "../../lib/datetime";
import { mailbox, mailErrorMessage } from "../../api/mailbox";
import type { MailPurgePreview } from "../../api/types";
import { formatBackupSize, formatCount, shiftTaipeiDate } from "./backupUsage";

// 清理備份(業主 2026-10-02 核可):只清**雲端已刪除**的信、只動自己的備份、依日期區段。
// 流程:選日期 → 試算(幾封、釋出多少) → 輸入「刪除」 → 永久刪除。改了日期就要重新試算。

export interface BackupPurgeDialogProps {
  open: boolean;
  onClose: () => void;
  /** 清理完成後(頁面重抓用量與列表)。 */
  onPurged: () => void;
}

export function BackupPurgeDialog({ open, onClose, onPurged }: BackupPurgeDialogProps) {
  const toast = useToast();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState<MailPurgePreview | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  const range = () => ({ date_from: from || undefined, date_to: to || undefined });
  const edit = (apply: () => void) => {
    apply();
    setPreview(null);
    setTyped("");
  };

  const close = () => {
    if (busy) return;
    setFrom("");
    setTo("");
    setPreview(null);
    setTyped("");
    onClose();
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      setPreview(await mailbox.purgePreview(range()));
      setTyped("");
    } catch (err) {
      toast.push(mailErrorMessage(err), "error");
    } finally {
      setBusy(false);
    }
  };

  const runPurge = async () => {
    setBusy(true);
    try {
      const got = await mailbox.purge(range());
      toast.push(`已清理 ${formatCount(got.deleted_count)} 封,釋出 ${formatBackupSize(got.freed_bytes)}`, "success");
      setBusy(false);
      setFrom("");
      setTo("");
      setPreview(null);
      setTyped("");
      onPurged();
      onClose();
    } catch (err) {
      setBusy(false);
      toast.push(mailErrorMessage(err), "error");
    }
  };

  const hasRange = from !== "" || to !== "";
  const canPurge = preview !== null && preview.count > 0 && typed === "刪除" && !busy;

  return (
    <Dialog open={open} onClose={close} title="清理備份" maxWidth="max-w-lg" closeOnBackdrop={false}>
      <div className="flex flex-col gap-3">
        <p className="m-0 text-sm text-[var(--text-2)]">
          只清「雲端已經刪除」的信,依信的日期選一段(台北時間,含頭尾兩天)。雲端還在的信不會動。
        </p>
        <div className="flex flex-wrap gap-3">
          <Input label="起日" type="date" value={from} onChange={(e) => edit(() => setFrom(e.target.value))} />
          <Input label="迄日" type="date" value={to} onChange={(e) => edit(() => setTo(e.target.value))} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => edit(() => setTo(shiftTaipeiDate(new Date(), { years: 1 })))}>
            一年前以前
          </Button>
          <Button size="sm" variant="ghost" onClick={() => edit(() => setTo(shiftTaipeiDate(new Date(), { months: 6 })))}>
            半年前以前
          </Button>
          <Button size="sm" variant="secondary" disabled={!hasRange || busy} onClick={() => void runPreview()}>
            試算
          </Button>
        </div>

        {preview ? (
          <div className="flex flex-col gap-2">
            <p className="m-0 text-sm font-medium text-[var(--text)]">
              這段期間雲端已刪除的信共 {formatCount(preview.count)} 封,清掉後釋出 {formatBackupSize(preview.bytes)}
            </p>
            {preview.samples.length > 0 ? (
              <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-[var(--text-3)]">
                {preview.samples.map((one) => (
                  <li key={one.id} className="truncate">
                    <span className="text-[var(--text-2)]">{one.subject || "(沒有主旨)"}</span>
                    {" · "}
                    {one.from_name || one.from_addr}
                    {" · "}
                    {formatTaipeiDateTime(one.date)}
                  </li>
                ))}
              </ul>
            ) : null}
            {preview.count > 0 ? (
              <>
                <p role="alert" className="m-0 text-sm font-semibold text-[var(--danger)]">
                  永久刪除,無法復原
                </p>
                <Input
                  label="輸入「刪除」確認"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                />
              </>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close} disabled={busy}>
            取消
          </Button>
          <Button variant="danger" disabled={!canPurge} onClick={() => void runPurge()}>
            確定清理
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
